/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import type { User } from 'firebase/auth';
import type { AlertColor } from '@mui/material';
import { useTranslation } from 'next-i18next';

import { IAppModalRef } from '@/components/common/modal';
import { useFirebaseAuth } from '../firebase/use-firebase-auth';
import { useUserContext } from '@/infrastructure/context/user-context';
import AppSnackbar, { IAppSnackbarRef } from '@/components/common/snackbar';
import { usePlaylist } from '@/infrastructure/context/playlist-context';
import { isAuthCriticalRoute } from '@/infrastructure/utils/route-policies';

const CreatePlaylistForm = dynamic(
  () => import('@/components/forms/create-playlist'),
  {
    ssr: false,
  }
);
const AppModal = dynamic(() => import('@/components/common/modal/dynamic'), {
  ssr: false,
});
const AppLoginForm = dynamic(() => import('@/components/forms/login'), {
  ssr: false,
});

export interface IAuthUserContextType {
  authUser: User | null;
  loading: boolean;
  showMessage: (message: string, severity?: AlertColor) => void;
  sendLinkToEmail: (email: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
  openCreatePlaylist: () => Promise<void>;
}

const authUserContext = React.createContext<IAuthUserContextType>({
  authUser: null,
  loading: true,
  showMessage: (message: string, severity?: AlertColor) => {},
  sendLinkToEmail: async (email: string) => {},
  signInWithGoogle: async () => {},
  signInWithApple: async () => {},
  signOut: async () => {},
  checkAuth: async () => {},
  openCreatePlaylist: async () => {},
});

export const useAuth = () => useContext(authUserContext);

interface IAuthUserProviderProps {
  children: React.ReactNode;
}

const AuthUserProvider: React.FC<IAuthUserProviderProps> = (props) => {
  const { children } = props;
  const router = useRouter();
  const { createPlaylist } = usePlaylist();
  const { loadUserInfo } = useUserContext();

  const shouldAutoInitAuth = isAuthCriticalRoute(router.pathname);

  const auth = useFirebaseAuth({ autoInit: shouldAutoInitAuth });
  const { authUser } = auth;

  const { t: tButtons } = useTranslation('buttons');

  const playlistCreateModal = useRef<IAppModalRef>(null);
  const modal = useRef<IAppModalRef>(null);
  const snackbar = useRef<IAppSnackbarRef>(null);

  const showMessage = (message: string, severity?: AlertColor) => {
    if (snackbar.current) {
      snackbar.current.open(message, severity);
    }
  };

  const checkAuth = async (): Promise<void> => {
    await auth.ensureAuthReady();
    const currentUser = auth.authUser || (await auth.getCurrentUser());

    return new Promise((res) => {
      const isAuthorized = !!currentUser;
      if (isAuthorized) res();
      if (!isAuthorized && modal.current) {
        modal.current.open();
      }
    });
  };

  const openCreatePlaylist = async () => {
    await checkAuth();
    if (playlistCreateModal.current) {
      playlistCreateModal.current.open();
    }
  };

  const onModalClose = () => {
    if (modal.current) {
      modal.current.close();
    }
  };

  const onSubmit = async (playlistName: string) => {
    if (authUser) {
      await createPlaylist({ user: authUser, playlistName });
      if (playlistCreateModal.current) {
        playlistCreateModal.current.close();
      }
      if (snackbar.current) {
        snackbar.current.open(tButtons('playlistCreated'));
      }
    }
  };

  useEffect(() => {
    if (authUser) {
      (async () => {
        await loadUserInfo(authUser);
      })();
    }
  }, [authUser]);

  return (
    <authUserContext.Provider
      value={{
        ...auth,
        checkAuth,
        openCreatePlaylist,
        showMessage,
      }}
    >
      <AppModal forwardedRef={playlistCreateModal}>
        <CreatePlaylistForm onSubmit={onSubmit} />
      </AppModal>
      <AppModal forwardedRef={modal}>
        <AppLoginForm onClose={onModalClose} />
      </AppModal>
      <AppSnackbar ref={snackbar} />
      {children}
    </authUserContext.Provider>
  );
};

export default AuthUserProvider;
